import React, { useState, useEffect } from 'react';
import { Search, Plus, Calendar as CalendarIcon, Clock, Users, Eye, Edit, Trash2, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { apiClient } from '../../utils/apiHelper';

const MeetingSchedule = () => {
  const [meetings, setMeetings] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    meeting_date: new Date().toISOString().split('T')[0],
    meeting_time: '10:00',
    end_time: '11:00',
    title: '',
    description: '',
    organizer: '',
    participants: '',
    participant_count: 0,
    location: '',
    meeting_type: 'Internal',
    agenda: '',
    status: 'Scheduled'
  });

  useEffect(() => { fetchMeetings(); }, []);

  const fetchMeetings = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/frontdesk/meetings');
      setMeetings(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching meetings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Calculate participant count from participants string
      const participantList = formData.participants.split(',').filter(p => p.trim());
      const submitData = {
        ...formData,
        participant_count: participantList.length
      };
      await apiClient.post('/api/frontdesk/meetings', submitData);
      alert('Meeting scheduled successfully!');
      setShowModal(false);
      resetForm();
      fetchMeetings();
    } catch (error) {
      alert('Failed to schedule meeting');
    }
  };

  const handleStatusChange = async (meetingId, status) => {
    try {
      await apiClient.put(`/api/frontdesk/meetings/${meetingId}/status?status=${status}`);
      fetchMeetings();
    } catch (error) {
      alert('Failed to update status');
    }
  };

  const handleDelete = async (meetingId) => {
    if (!window.confirm('Are you sure you want to delete this meeting?')) return;
    try {
      await apiClient.delete(`/api/frontdesk/meetings/${meetingId}`);
      fetchMeetings();
    } catch (error) {
      alert('Failed to delete meeting');
    }
  };

  const resetForm = () => {
    setFormData({
      meeting_date: new Date().toISOString().split('T')[0],
      meeting_time: '10:00',
      end_time: '11:00',
      title: '',
      description: '',
      organizer: '',
      participants: '',
      participant_count: 0,
      location: '',
      meeting_type: 'Internal',
      agenda: '',
      status: 'Scheduled'
    });
  };

  const filteredMeetings = meetings.filter(m =>
    m.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.organizer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.location?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Meeting Schedule</CardTitle>
          <Button onClick={() => setShowModal(true)}>
            <Plus className="h-4 w-4 mr-2" /> Schedule Meeting
          </Button>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <Input
                type="text"
                placeholder="Search by title, organizer, or location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Time</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Meeting Title</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Organizer</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Participants</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Location</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredMeetings.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center p-8 text-gray-500">
                      {loading ? 'Loading...' : 'No meetings scheduled. Click "Schedule Meeting" to create a new meeting.'}
                    </td>
                  </tr>
                ) : (
                  filteredMeetings.map((meeting) => (
                    <tr key={meeting.id} className="hover:bg-gray-50">
                      <td className="p-3 text-sm">
                        <div className="flex items-center gap-2">
                          <CalendarIcon className="h-4 w-4 text-gray-400" />
                          {meeting.meeting_date}
                        </div>
                      </td>
                      <td className="p-3 text-sm">
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-gray-400" />
                          {meeting.meeting_time} - {meeting.end_time || ''}
                        </div>
                      </td>
                      <td className="p-3 text-sm font-medium">{meeting.title}</td>
                      <td className="p-3 text-sm">{meeting.organizer}</td>
                      <td className="p-3 text-sm">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-gray-400" />
                          {meeting.participant_count || 0}
                        </div>
                      </td>
                      <td className="p-3 text-sm">{meeting.location}</td>
                      <td className="p-3">
                        <Select value={meeting.status} onValueChange={(v) => handleStatusChange(meeting.id, v)}>
                          <SelectTrigger className="w-28 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Scheduled">Scheduled</SelectItem>
                            <SelectItem value="Ongoing">Ongoing</SelectItem>
                            <SelectItem value="Completed">Completed</SelectItem>
                            <SelectItem value="Cancelled">Cancelled</SelectItem>
                            <SelectItem value="Postponed">Postponed</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          <button onClick={() => handleDelete(meeting.id)} className="text-red-600 hover:text-red-800">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Schedule Meeting Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Schedule New Meeting</h2>
              <button onClick={() => setShowModal(false)}><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Date *</Label>
                  <Input type="date" required value={formData.meeting_date} onChange={(e) => setFormData({...formData, meeting_date: e.target.value})} />
                </div>
                <div>
                  <Label>Start Time *</Label>
                  <Input type="time" required value={formData.meeting_time} onChange={(e) => setFormData({...formData, meeting_time: e.target.value})} />
                </div>
                <div>
                  <Label>End Time</Label>
                  <Input type="time" value={formData.end_time} onChange={(e) => setFormData({...formData, end_time: e.target.value})} />
                </div>
              </div>
              <div>
                <Label>Meeting Title *</Label>
                <Input required value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Organizer *</Label>
                  <Input required value={formData.organizer} onChange={(e) => setFormData({...formData, organizer: e.target.value})} />
                </div>
                <div>
                  <Label>Location *</Label>
                  <Input required value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Meeting Type</Label>
                  <Select value={formData.meeting_type} onValueChange={(v) => setFormData({...formData, meeting_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Internal">Internal</SelectItem>
                      <SelectItem value="External">External</SelectItem>
                      <SelectItem value="Virtual">Virtual</SelectItem>
                      <SelectItem value="Board Meeting">Board Meeting</SelectItem>
                      <SelectItem value="Staff Meeting">Staff Meeting</SelectItem>
                      <SelectItem value="Parent Meeting">Parent Meeting</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Participants (comma separated)</Label>
                  <Input 
                    value={formData.participants} 
                    onChange={(e) => setFormData({...formData, participants: e.target.value})} 
                    placeholder="John, Jane, Mike..."
                  />
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Input value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} />
              </div>
              <div>
                <Label>Agenda</Label>
                <textarea 
                  className="w-full p-2 border rounded-lg" 
                  rows="3"
                  value={formData.agenda} 
                  onChange={(e) => setFormData({...formData, agenda: e.target.value})}
                  placeholder="Meeting agenda items..."
                />
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                <Button type="submit">Schedule Meeting</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeetingSchedule;
